import { useCallback, useRef } from "react";

import {
  createMultipartUpload,
  MultipartUploadHandle,
  UploadedMedia,
} from "@/client/media-upload";

export interface MultipartUpload {
  /** Begin a fresh upload for `file`. Resolves with the media, or throws. */
  start: (file: File) => Promise<UploadedMedia>;
  /**
   * Resume the current upload after a failure. `@uppy/aws-s3` re-signs only the
   * parts that did not land, against the same `uploadId` — so a network drop
   * costs only the unfinished parts, not the whole transfer. Throws if there is
   * no upload to resume.
   */
  resume: () => Promise<UploadedMedia>;
  /** Clean up the current upload and forget its handle. */
  reset: () => void;
}

/**
 * React lifecycle wrapper around a single resumable S3 multipart upload.
 *
 * Holds the Uppy handle in a ref so `resume()` re-signs only the parts that did
 * not land instead of restarting the transfer, and cancels it on unmount. The
 * upload is "resilient" because Uppy keeps the `uploadId` alive across failures
 * — the caller decides when to {@link resume} (e.g. a "Retry upload" button).
 */
export function useMultipartUpload(): MultipartUpload {
  const handleRef = useRef<MultipartUploadHandle | null>(null);

  // Forget the handle — guarding against a newer start() that replaced the ref
  // while this one was completing.
  const release = useCallback((handle: MultipartUploadHandle) => {
    if (handleRef.current === handle) {
      handleRef.current = null;
    }
  }, []);

  const start = useCallback(
    async (file: File) => {
      handleRef.current?.cancel();
      const handle = createMultipartUpload(file);
      handleRef.current = handle;
      const media = await handle.start();
      release(handle);
      return media;
    },
    [release]
  );

  const resume = useCallback(() => {
    const handle = handleRef.current;
    if (handle == null) {
      throw new Error("No multipart upload to resume");
    }
    return handle.resume().then((media) => {
      release(handle);
      return media;
    });
  }, [release]);

  const reset = useCallback(() => {
    handleRef.current?.cancel();
    handleRef.current = null;
  }, []);

  return { start, resume, reset };
}
